/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { PrimaryButton, SearchInput } from "../../components";
import { useLocation, useNavigate } from "react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { LotteryTable } from "./LotteryTable";
import { useLottery } from "../../store/useLottery";
import { usePartners } from "../../store/usePartners";

const SEARCH_DEBOUNCE_MS = 300;

function LotteryScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const location = useLocation();
  const { allLotteries, getAllLotteries, hasNextPage } = useLottery();
  const { allPartners, getAllPartners } = usePartners();
  /** State */
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState("");
  const [partnerId, setPartnerId] = useState<string>("");
  const [activeOnly, setActiveOnly] = useState(false);
  const isFirstSearchRun = useRef(true);

  const setQueryParams = useCallback(
    (next: {
      page?: number;
      search?: string;
      partnerId?: string;
      activeOnly?: boolean;
    }) => {
      const params = new URLSearchParams(location.search);
      const nextPage = next.page ?? page;
      const nextSearch = next.search ?? search;
      const nextPartnerId = next.partnerId ?? partnerId;
      const nextActiveOnly = next.activeOnly ?? activeOnly;

      if (nextPage && nextPage !== 1) params.set("page", String(nextPage));
      else params.delete("page");

      if (nextSearch?.trim()) params.set("search", nextSearch.trim());
      else params.delete("search");

      if (nextPartnerId?.trim()) params.set("partnerId", nextPartnerId.trim());
      else params.delete("partnerId");

      if (nextActiveOnly) params.set("activeOnly", "true");
      else params.delete("activeOnly");

      const nextSearchString = params.toString();
      const current = location.search.startsWith("?")
        ? location.search.slice(1)
        : location.search;
      if (nextSearchString !== current) {
        navigate(
          { pathname: location.pathname, search: nextSearchString ? `?${nextSearchString}` : "" },
          { replace: true }
        );
      }
    },
    [location.pathname, location.search, navigate, page, partnerId, search]
  );

  /** Effects: initial load and when search changes (backend filter) */
  useEffect(() => {
    getAllPartners(1, 1000);
  }, []);

  // Sync state from URL (back/forward, direct link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qSearch = params.get("search") ?? "";
    const qPartnerId = params.get("partnerId") ?? "";
    const qPageRaw = params.get("page");
    const qPage = qPageRaw ? Math.max(1, Number(qPageRaw) || 1) : 1;
    const qActiveOnlyRaw = (params.get("activeOnly") ?? "").toLowerCase();
    const qActiveOnly = qActiveOnlyRaw === "true" || qActiveOnlyRaw === "1";

    setSearch((prev) => (prev === qSearch ? prev : qSearch));
    setPartnerId((prev) => (prev === qPartnerId ? prev : qPartnerId));
    setPage((prev) => (prev === qPage ? prev : qPage));
    setActiveOnly((prev) => (prev === qActiveOnly ? prev : qActiveOnly));
  }, [location.search]);

  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      getAllLotteries(1, 10, search, partnerId, activeOnly);
      return;
    }
    const t = setTimeout(() => {
      getAllLotteries(1, 10, search, partnerId, activeOnly);
      setPage(1);
      setQueryParams({ page: 1, search, partnerId, activeOnly });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, partnerId, activeOnly]);

  // When page changes via URL or load more, fetch that page
  useEffect(() => {
    if (page <= 1) return;
    getAllLotteries(page, 10, search, partnerId, activeOnly);
  }, [page]);

  /** Callbacks */
  const addLottery = () => navigate(`/add-lottery`);
  const loadMore = useCallback(() => {
    const next = page + 1;
    setPage(next);
    setQueryParams({ page: next, search, partnerId, activeOnly });
    getAllLotteries(next, 10, search, partnerId, activeOnly);
  }, [page, search, partnerId, activeOnly, getAllLotteries]);

  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-5 sm:mb-11">
          Lotteries
        </h1>
        <PrimaryButton label="Add new" onClick={addLottery} className="h-10" />
      </div>
      <div className="w-full px-7 py-3 bg-white rounded-xl shadow-none mb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title..."
            />
          </div>
          <div className="sm:w-[320px]">
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="w-full h-[46px] px-4 bg-sky-lighter border border-gray-border focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none"
            >
              <option value="">All partners</option>
              {(allPartners ?? [])
                .filter((p) => !p?.isCourier)
                .map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name ?? "-"}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 sm:ml-2 select-none">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
              }}
              className="h-4 w-4 border-gray-300 rounded focus:outline-none focus:ring-0"
            />
            <span className="font-satoshi text-sm text-dark">Only active</span>
          </label>
        </div>
      </div>
      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        {!allLotteries?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no Lottery created yet.
          </p>
        ) : (
          <LotteryTable />
        )}
      </div>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={loadMore}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(LotteryScreen);
